import { Button, Divider, Menu, Stack, Typography, SxProps, Box } from '@mui/material';
import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    IUserData,
    getUserProfile,
    logoutUser,
    updateUserProfilePicture,
} from '../../../../redux/user/sliceUserProfile';
import { IAppReduxStore } from '../../../../redux/store';
import { vars, ceraProRegular } from '../../../../Theme';
import UserAvatar from '../UserAvatar';
import SettingsDialog from '../../Dialogs/SettingsDialog';

import { useEnqueueSnackbar } from '../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../Toast/Toast';
import { settingsDataObject } from './UserSettingsDialogConfig';
import useBreakpoints from '../../../../hooks/useBreakpoint';

/** Base Typography styles for mutual fonts. */
const fontSx: SxProps = {
    ...ceraProRegular,
    fontWeight: 700,
};

/** Base Typography styles for Manage Account and Logout. */
const typographySx: SxProps = {
    ...ceraProRegular,
    color: vars.font,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
};

/** Base Button styles. */
const buttonSx: SxProps = {
    color: vars.fillGray,
    textTransform: 'none',
    p: 1,
    width: '90%',
    justifyContent: 'start',
    '&:hover': {
        backgroundColor: vars.fillGray,
    },
};

/** Base Stack wrapper styles. */
const stackSx: SxProps = {
    width: '100%',
    display: 'flex',
    pl: 2,
    pr: 2,
};

/**
 * Component that shows the user's Auth0 profile image and allows them to manage and/or log out of their account.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'header' });
    const { isMobile } = useBreakpoints();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Access relevant state variables from the redux store. */
    const {
        name: username,
        first_name,
        last_name,
        email,
    } = useSelector((store: IAppReduxStore) => store.userProfile.data ?? ({} as IUserData));
    const pictureLoading = useSelector(
        (store: IAppReduxStore) => store.userProfile.picture?.loading,
    );
    const dataLoading = useSelector((store: IAppReduxStore) => store.userProfile?.loading);
    const errorPictureUploading = useSelector(
        (store: IAppReduxStore) => store.userProfile.picture?.error,
    );

    const [loadingAttempts, setLoadingAttempts] = useState<number>(0);

    /** State slice that controls whether the user popup is shown. */
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    /** State slice that controls whether the settings dialog is shown. */
    const [openSettingsDialog, setOpenSettingsDialog] = React.useState<boolean>(false);

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    /** Display upload errors for the User Avatar as the queue of Snackbars. */
    useEffect(() => {
        errorPictureUploading &&
            enqueueSnackbar(t('pictureUploadingError'), {
                additionalInfo: errorPictureUploading.message,
                severity: ToastSeverity.Error,
            });
    }, [errorPictureUploading]);

    /**
     * Fetches the user's profile data when the component first renders.
     */
    useEffect(() => {
        if (!username && !dataLoading && loadingAttempts < 3) {
            setLoadingAttempts(prev => prev + 1);
            dispatch(getUserProfile());
        }
    }, [username, dataLoading]);

    /**
     * Makes the menu visible.
     * @param e - React Click Event.
     */
    const handleUserBtnClick = (e: React.MouseEvent<HTMLButtonElement>) =>
        setAnchorEl(e.currentTarget);

    /**
     * Logs the user out of the application.
     */
    const handleLogout = () => {
        dispatch(logoutUser());
    };

    /**
     * Opens settings dialog.
     */
    const handleClickSettingsDialogOpen = () => {
        setOpenSettingsDialog(true);
    };

    /**
     * Closes settings dialog.
     */
    const handleCloseSettingsDialog = () => {
        setOpenSettingsDialog(false);
    };

    /**
     * Updates User's Avatar.
     * @param e React Change Event.
     */
    const handleChangeImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(updateUserProfilePicture(e.target.files[0]));
    };

    return (
        <>
            <Button
                sx={{
                    '&:hover': { backgroundColor: 'transparent' },
                    alignItems: 'center',
                    paddingLeft: isMobile ? '16px' : '8px',
                    paddingRight: isMobile ? '16px' : '24px',
                    paddingY: '8px',
                    minWidth: '40px',
                }}
                onClick={handleUserBtnClick}
            >
                <UserAvatar size={40} loading={dataLoading} />
            </Button>

            <Menu open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} anchorEl={anchorEl}>
                <Stack
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        maxWidth: 344,
                    }}
                >
                    <Stack
                        sx={{
                            ...stackSx,
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        <Box>
                            <UserAvatar size={60} />
                        </Box>

                        <Stack
                            sx={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                p: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    ...fontSx,
                                    fontSize: 16,
                                    lineHeight: '22.4px',
                                }}
                            >
                                {username}
                            </Typography>
                            <Typography
                                sx={{
                                    ...fontSx,
                                    fontSize: 14,
                                    color: vars.concrete,
                                    lineHeight: '19.6px',
                                }}
                            >
                                {email}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Divider sx={{ width: '100%' }} />
                    <Stack
                        sx={{
                            ...stackSx,
                            flexDirection: 'column',
                        }}
                    >
                        <Button onClick={handleClickSettingsDialogOpen} sx={buttonSx}>
                            <Typography sx={typographySx}>{t('manageAccount')}</Typography>
                        </Button>

                        <Button onClick={handleLogout} sx={buttonSx}>
                            <Typography sx={typographySx}>{t('logout')}</Typography>
                        </Button>
                    </Stack>
                </Stack>
            </Menu>
            <SettingsDialog
                open={openSettingsDialog}
                handleClose={handleCloseSettingsDialog}
                data={settingsDataObject({
                    username,
                    first_name,
                    last_name,
                    email,
                    pictureLoading,
                    handleChangeImage,
                    t,
                })}
            />
        </>
    );
};
